import { Track } from "./schema";

const PLAYER_STATE_KEY = 'musicPlayer.state';
const QUEUE_STATE_KEY = 'musicPlayer.queue';

export interface PlayerState {
  trackId: string | null;
  isPlaying: boolean;
  sourceTrackIds: string[];
  currentTime?: number; // Current playback position in seconds
}

export interface QueueStateData {
  trackIds: string[];
  currentTrackIndex: number;
  sourceTrackIds: string[];
  sourceTrackIndex: number;
}

export function savePlayerState(state: PlayerState): void {
  try {
    localStorage.setItem(PLAYER_STATE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error('Failed to save player state:', e);
  }
}

export function loadPlayerState(): PlayerState | null {
  try {
    const saved = localStorage.getItem(PLAYER_STATE_KEY);
    if (!saved) return null;
    return JSON.parse(saved) as PlayerState;
  } catch (e) {
    console.error('Failed to load player state:', e);
    return null;
  }
}

export function clearPlayerState(): void {
  try {
    localStorage.removeItem(PLAYER_STATE_KEY);
  } catch (e) {
    console.error('Failed to clear player state:', e);
  }
}

export function restorePlayerStateFromTracks(
  state: PlayerState | null,
  allTracks: Track[]
): { track: Track | null; sourceTracks: Track[] } {
  if (!state || !state.trackId) {
    return { track: null, sourceTracks: [] };
  }

  const track = allTracks.find(t => t.id === state.trackId) || null;
  const sourceTracks = state.sourceTrackIds
    .map(id => allTracks.find(t => t.id === id))
    .filter((t): t is Track => t !== undefined);

  return { track, sourceTracks };
}

export function saveQueueState(state: QueueStateData): void {
  try {
    localStorage.setItem(QUEUE_STATE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error('Failed to save queue state:', e);
  }
}

export function loadQueueState(): QueueStateData | null {
  try {
    const saved = localStorage.getItem(QUEUE_STATE_KEY);
    if (!saved) return null;
    return JSON.parse(saved) as QueueStateData;
  } catch (e) {
    console.error('Failed to load queue state:', e);
    return null;
  }
}

export function clearQueueState(): void {
  try {
    localStorage.removeItem(QUEUE_STATE_KEY);
  } catch (e) {
    console.error('Failed to clear queue state:', e);
  }
}

export function restoreQueueStateFromTracks(
  state: QueueStateData | null,
  allTracks: Track[]
): { tracks: Track[]; currentTrackIndex: number; sourceTracks: Track[]; sourceTrackIndex: number } | null {
  if (!state) return null;

  const tracks = state.trackIds
    .map(id => allTracks.find(t => t.id === id))
    .filter((t): t is Track => t !== undefined);
  
  const sourceTracks = state.sourceTrackIds
    .map(id => allTracks.find(t => t.id === id))
    .filter((t): t is Track => t !== undefined);

  if (tracks.length === 0) return null;

  return {
    tracks,
    currentTrackIndex: Math.min(state.currentTrackIndex, tracks.length - 1),
    sourceTracks,
    sourceTrackIndex: Math.min(state.sourceTrackIndex, sourceTracks.length - 1),
  };
}

